import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Button from 'gu-ui-kit/Button'
import Grid from 'gu-ui-kit/Grid/Container'
import ReactPortal from 'gu-ui-kit/ReactPortal'
import styles from 'components/SidePanel/SidePanel.module.scss'
import PageHeader from 'components/PageHeader'
import Icon from 'gu-ui-kit/Icon'
import Col from 'gu-ui-kit/Grid/Col'
import Typography from 'gu-ui-kit/Typography'
const PANEL_TRANSITION_MS = 250

const SidePanel = ({
  open = false,
  onClose,
  children = null,
  title = null,
  subtitle = null,
  closeButton = true,
  scrollable = true,
}) => {
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const containerRef = useRef(null)
  const closeTimerRef = useRef(null)

  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }

      setMounted(true)

      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true))
      })

      return () => cancelAnimationFrame(rafId)
    }

    setVisible(false)
  }, [open])

  const unmountPanel = useCallback(() => {
    setMounted(false)
  }, [])

  useEffect(() => {
    if (!mounted || visible) {
      return
    }

    closeTimerRef.current = setTimeout(unmountPanel, PANEL_TRANSITION_MS)

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }
  }, [mounted, visible, unmountPanel])

  const handleTransitionEnd = useCallback(
    (event) => {
      if (event.target !== containerRef.current || visible) {
        return
      }

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }

      unmountPanel()
    },
    [visible, unmountPanel],
  )

  useEffect(() => {
    if (!mounted || !visible) {
      return
    }

    document.body.classList.add('body_overflow_hidden')

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.classList.remove('body_overflow_hidden')
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [mounted, visible, onClose])

  if (!mounted) {
    return null
  }

  return (
    <ReactPortal wrapperId={'side-panel-portal'}>
      <div
        className={styles['side-panel']}
        style={{ '--side-panel-transition-duration': PANEL_TRANSITION_MS + 'ms' }}
      >
        <div
          className={clsx(styles['side-panel__overlay'], {
            [styles['side-panel__overlay_open']]: visible,
          })}
          role={'presentation'}
          onClick={onClose}
        />
        <aside
          ref={containerRef}
          className={clsx(styles['side-panel__container'], {
            [styles['side-panel__container_open']]: visible,
          })}
          onTransitionEnd={handleTransitionEnd}
        >
          <Grid
            className={clsx(styles['side-panel__body'], 'layout-tile')}
            direction={'column'}
            gap={40}
            wrap={false}
          >
            {(title || subtitle || closeButton) && (
              <Grid
                alignItems={'start'}
                className={styles['side-panel__header']}
                direction={'row'}
                gap={16}
                justifyContent={!title && !subtitle ? 'end' : 'between'}
                wrap={false}
              >
                {(title || subtitle) && (
                  <Grid
                    direction={'column'}
                    gap={16}
                  >
                    {title && <PageHeader>{title}</PageHeader>}
                    {subtitle && (
                      <Typography
                        noMargin
                        color={'Label-Secondary'}
                        component={'div'}
                        variant={'Body-20'}
                      >
                        {subtitle}
                      </Typography>
                    )}
                  </Grid>
                )}
                {closeButton && (
                  <Col>
                    <Button
                      grey
                      noLabel
                      startIcon={
                        <Icon
                          color={'Interface-Seventh'}
                          name={'close'}
                          width={20}
                        />
                      }
                      variant={'ghost'}
                      onClick={onClose}
                    />
                  </Col>
                )}
              </Grid>
            )}
            <div
              className={clsx(styles['side-panel__content'], {
                [styles['side-panel__content_scrollable']]: scrollable,
                [styles['side-panel__content_fixed']]: !scrollable,
              })}
            >
              {children}
            </div>
          </Grid>
        </aside>
      </div>
    </ReactPortal>
  )
}

SidePanel.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  closeButton: PropTypes.bool,
  scrollable: PropTypes.bool,
}

export default SidePanel
